import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import ShohAI from '../components/ShohAI';
import axios from 'axios';

interface Job {
  _id: string;
  title: string;
  description: string;
  serviceType: string;
  location: {
    address: string;
    city: string;
  };
  schedule: {
    startDate: string;
    endDate: string;
    preferredTime: string;
  };
  contract: {
    type: string;
    rate: number;
  };
  customer: {
    firstName: string;
    lastName: string;
  };
  status: string;
}

const CaregiverJobs: React.FC = () => {
  usePageTitle('My Jobs');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [myJobs, setMyJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'available' | 'accepted' | 'history'>('accepted');

  useEffect(() => {
    fetchJobs();
    fetchMyJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs?status=pending');
      setJobs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchMyJobs = async () => {
    try {
      if (!user?.id) return;
      const response = await axios.get(`/api/jobs/caregiver/${user.id}`);
      setMyJobs(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching my jobs:', error);
      setLoading(false);
    }
  };

  const acceptJob = async (jobId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/jobs/${jobId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobs();
      fetchMyJobs();
      alert('Job accepted successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error accepting job');
    }
  };

  const declineJob = async (jobId: string) => {
    const reason = prompt('Please provide a reason for declining (optional):');
    try {
      await axios.post(`/api/jobs/${jobId}/decline`, { declineReason: reason || 'Not specified' });
      fetchJobs();
      fetchMyJobs();
      alert('Job declined');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error declining job');
    }
  };

  const getServiceBadgeColor = (type: string): string => {
    const colors: Record<string, string> = {
      'nurse-care': 'bg-red-100 text-red-800',
      'everyday-assistance': 'bg-primary-100 text-primary-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col animate-fade-in">
      {/* Header */}

      {/* Hero Header */}
      <div className="bg-white shadow-sm border-b sticky top-12 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
             <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Your Assignments</h1>
                <p className="text-sm text-gray-500 mt-1">Manage active contracts & new requests</p>
             </div>
             <div className="hidden sm:block">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
             </div>
          </div>

          {/* Modern Tabs */}
          <div className="flex p-1 bg-gray-100/80 rounded-xl">
            <button
              onClick={() => setActiveTab('accepted')}
              className={`flex-1 flex items-center justify-center py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'accepted'
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
              }`}
            >
              <span className="flex items-center gap-2">
                Active Jobs
                {myJobs.filter(job => job.status === 'in-progress' || job.status === 'accepted').length > 0 && (
                   <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                      {myJobs.filter(job => job.status === 'in-progress' || job.status === 'accepted').length}
                   </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('available')}
              className={`flex-1 flex items-center justify-center py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'available'
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
              }`}
            >
               <span className="flex items-center gap-2">
                New Requests
                {jobs.length > 0 && (
                   <span className="bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full text-xs">
                      {jobs.length}
                   </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 flex items-center justify-center py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'history'
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
              }`}
            >
              <span className="flex items-center gap-2">
                Past Jobs
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 flex-1 w-full pb-24">
        {activeTab === 'accepted' && (
          <div className="space-y-4">
            {myJobs.filter(job => job.status === 'in-progress' || job.status === 'accepted').length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center border border-gray-100 shadow-sm">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">No Active Jobs</h3>
                <p className="text-gray-500 mt-2">You don't have any ongoing jobs right now.</p>
                <button
                  onClick={() => setActiveTab('available')}
                  className="mt-6 px-6 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                >
                  Browse Available Jobs
                </button>
              </div>
            ) : (
               myJobs
                 .filter(job => job.status === 'in-progress' || job.status === 'accepted')
                 .map(job => (
                   <div key={job._id} className="bg-white rounded-xl shadow-sm border border-green-100 p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-green-50 rounded-bl-full -mr-4 -mt-4 z-0"></div>
                      
                      <div className="relative z-10">
                         {/* Header */}
                         <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                               <div className="p-2.5 rounded-xl bg-green-50 text-green-600 shadow-sm">
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                               </div>
                               <div>
                                  <h3 className="text-lg font-bold text-gray-900 leading-tight">{job.title}</h3>
                                  <div className="flex items-center text-sm text-gray-500 mt-1">
                                     <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                                     Active Contract
                                  </div>
                               </div>
                            </div>
                            <div className="text-right">
                               <div className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">Customer</div>
                               <div className="text-sm font-semibold text-gray-900">{job.customer.firstName} {job.customer.lastName}</div>
                            </div>
                         </div>

                         {/* Details Grid */}
                         <div className="grid grid-cols-2 gap-3 mb-5">
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                               <div className="flex items-center gap-2 mb-1">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span className="text-xs font-medium text-gray-500 uppercase">Location</span>
                               </div>
                               <p className="text-sm font-semibold text-gray-900 truncate">{job.location.city}</p>
                            </div>
                             <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                               <div className="flex items-center gap-2 mb-1">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-xs font-medium text-gray-500 uppercase">Schedule</span>
                               </div>
                               <p className="text-sm font-semibold text-gray-900 truncate">{job.schedule.preferredTime}</p>
                            </div>
                         </div>
                         
                         <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-5 flex items-center justify-between">
                            <div>
                               <span className="text-xs text-green-700 font-bold uppercase tracking-wide block mb-0.5">Contract Value</span>
                               <span className="text-lg font-bold text-green-800">৳{job.contract.rate.toLocaleString()}</span>
                               <span className="text-xs font-medium text-green-600">/{job.contract.type === 'weekly' ? 'week' : job.contract.type === 'monthly' ? 'month' : job.contract.type}</span>
                            </div>
                            <div className="h-10 w-px bg-green-200 mx-4"></div>
                            <div className="flex-1">
                               <span className="text-xs text-green-700 font-bold uppercase tracking-wide block mb-0.5">Dates</span>
                               <span className="text-sm font-semibold text-green-900">
                                  {new Date(job.schedule.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - {new Date(job.schedule.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                               </span>
                            </div>
                         </div>

                         {/* Actions */}
                         <button
                           onClick={() => navigate(`/jobs/${job._id}`)}
                           className="w-full py-3 bg-white border border-green-200 text-green-700 rounded-xl hover:bg-green-50 hover:border-green-300 font-bold text-sm transition-all shadow-sm flex items-center justify-center gap-2 group"
                         >
                           <span>View Job Details</span>
                           <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                           </svg>
                         </button>
                      </div>
                   </div>
                 ))
            )}
          </div>
        )}

        {activeTab === 'available' && (
          <div className="space-y-4">
            {jobs.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center border border-gray-100 shadow-sm">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">No New Requests</h3>
                <p className="text-gray-500 mt-2">You don't have any pending job requests at the moment.</p>
              </div>
            ) : (
              jobs.map(job => (
                <div key={job._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  {/* Card Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${job.serviceType === 'nurse-care' ? 'bg-red-50 text-red-600' : 'bg-primary-50 text-primary-600'}`}>
                        {job.serviceType === 'nurse-care' ? (
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                        <div className="flex items-center text-sm text-gray-500 mt-0.5">
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {job.location.city}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${job.serviceType === 'nurse-care' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                          {job.serviceType.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                       </span>
                    </div>
                  </div>

                  {/* Clean Description */}
                  <div className="mb-5 bg-gray-50 rounded-lg p-3">
                     <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                        {job.description ? job.description.split('---------- PATIENT DETAILS ----------')[0].trim() : "No additional details provided."}
                     </p>
                  </div>

                  {/* Key Details Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                       <span className="text-xs text-gray-500 block uppercase tracking-wide">Start Date</span>
                       <span className="text-sm font-semibold text-gray-900">{new Date(job.schedule.startDate).toLocaleDateString()}</span>
                    </div>
                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                       <span className="text-xs text-gray-500 block uppercase tracking-wide">Schedule</span>
                       <span className="text-sm font-semibold text-gray-900">{job.schedule.preferredTime}</span>
                    </div>
                    <div className="col-span-2 bg-gradient-to-r from-primary-50 to-white p-2.5 rounded-lg border border-primary-100">
                       <div className="flex justify-between items-center">
                          <span className="text-xs text-primary-700 font-bold uppercase tracking-wide">Offered Rate</span>
                          <span className="text-lg font-bold text-primary-700">৳{job.contract.rate.toLocaleString()}<span className="text-xs font-normal text-primary-600"> (Total)</span></span>
                       </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/jobs/${job._id}`)}
                      className="flex-1 py-2.5 px-4 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-300 font-medium text-sm transition-all"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => declineJob(job._id)}
                      className="py-2.5 px-4 bg-white border border-red-100 text-red-600 rounded-lg hover:bg-red-50 font-medium text-sm transition-all"
                    >
                      Skip
                    </button>
                    <button
                      onClick={() => acceptJob(job._id)}
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                    >
                      Accept Job
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
             {myJobs.filter(job => ['completed', 'cancelled', 'terminated', 'declined'].includes(job.status)).length === 0 ? (
               <div className="bg-white rounded-xl p-12 text-center border border-gray-100 shadow-sm">
                 <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                   <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                   </svg>
                 </div>
                 <h3 className="text-lg font-medium text-gray-900">No Past Jobs</h3>
                 <p className="text-gray-500 mt-2">You don't have any completed or past jobs yet.</p>
               </div>
             ) : (
               myJobs
                 .filter(job => ['completed', 'cancelled', 'terminated', 'declined'].includes(job.status))
                 .map(job => (
                   <div key={job._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 relative opacity-80 hover:opacity-100">
                     
                     <div className="absolute top-6 right-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'terminated' ? 'bg-red-100 text-red-800' :
                          job.status === 'declined' ? 'bg-gray-200 text-gray-600' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status === 'terminated' ? 'Terminated' : job.status}
                        </span>
                     </div>
                     
                     <div className="flex items-center gap-3 mb-4">
                         <div className="p-2 rounded-lg bg-gray-100 text-gray-500">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                         </div>
                         <div>
                            <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                            <p className="text-sm text-gray-500">
                               {new Date(job.schedule.startDate).toLocaleDateString()} - {new Date(job.schedule.endDate).toLocaleDateString()}
                            </p>
                         </div>
                     </div>

                     <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                           <span className="text-xs text-gray-500 block uppercase tracking-wide">Customer</span>
                           <span className="text-sm font-semibold text-gray-900">{job.customer.firstName} {job.customer.lastName}</span>
                        </div>
                        <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                           <span className="text-xs text-gray-500 block uppercase tracking-wide">Location</span>
                           <span className="text-sm font-semibold text-gray-900">{job.location.city}</span>
                        </div>
                     </div>

                     <button
                       onClick={() => navigate(`/jobs/${job._id}`)}
                       className="w-full py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-all flex items-center justify-center gap-2"
                     >
                       <span>View Details</span>
                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                       </svg>
                     </button>
                   </div>
                 ))
             )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="hidden sm:block bg-gray-900 text-white py-8 px-4 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <h4 className="text-2xl font-bold font-proyas tracking-wider mb-2">PROYAS</h4>
          <p className="text-gray-400 text-sm">Quality care, delivered with compassion</p>
          
          {/* Contact Info */}
          <div className="flex justify-center items-center gap-6 mt-4">
            <a href="mailto:contact@proyas.net" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              <span className="text-sm">contact@proyas.net</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61586633764841" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm">Facebook</span>
            </a>
          </div>
          
          <p className="text-gray-500 text-xs mt-4">© 2026 <span className="font-proyas tracking-wider">PROYAS</span>. All rights reserved.</p>
        </div>
      </footer>

      {/* AI Chatbot */}
      <ShohAI />
    </div>
  );
};

export default CaregiverJobs;
